import React, { useEffect, useMemo } from 'react';

import { Box } from '@rbilabs/universal-components';
import { Redirect, useGlobalSearchParams, usePathname } from 'expo-router';

import { BlankScreen } from 'components/blank-screen';
import { CardModal, CardModalProps } from 'components/card-modal';
import LoadingContainer from 'components/loading-animation/loading-container';
import { useNavigation } from 'hooks/navigation/use-navigation';
import useEffectOnce from 'hooks/use-effect-once';
import { routes as appRoutes } from 'navigation/routing';
import LoyaltyLoader from 'pages/loyalty/loyalty-loader/loyalty-loader';
import NotFound from 'pages/not-found';
import { RenderCurrentStaticPage } from 'pages/static/render-current-static-page';
import { IStaticPageRoute } from 'remote/queries/static-page';
import { useLocale } from 'state/intl';
import { PROD_DEFAULT_LANGUAGE, PROD_SUPPORTED_LANGUAGES } from 'state/intl/constants';
import { usePageManagerContext } from 'state/static-page-manager';
import { useGetStaticPageByRoute } from 'state/static-page-manager/hooks/use-get-static-page-by-route';
import { isRedirectedToSimplexUrl } from 'utils/simplex';

import { StaticPageSkeleton } from './static-page.skeleton';

const StaticPageContainer = () => {
  const { routesLoading, loadRoutes, loadRoutesHasBeenCalled } = usePageManagerContext();
  useEffectOnce(() => {
    // If the loadRoutes query has not been called yet, load the static routes list
    if (!loadRoutesHasBeenCalled) {
      loadRoutes();
    }
  });
  if (!loadRoutesHasBeenCalled || routesLoading) {
    return (
      <CardModalWithOptions>
        <LoyaltyLoader />
      </CardModalWithOptions>
    );
  }
  return <StaticPage />;
};

export const StaticPage = () => {
  const { currentPage, retrieveStaticPageById, routes, routesLoading } = usePageManagerContext();
  const { language: currentLanguage, region, setCurrentLocale } = useLocale();
  const { navigate } = useNavigation();

  const params = useGlobalSearchParams<{ lang: string }>();
  const pathname = usePathname();

  const { lang: routeLang } = params || {};

  // Remove first and last slash to check the "staticPath" and convert to lower case
  const staticPath = (pathname || '').replace(/^\/|\/$/g, '').toLowerCase();

  const targetPage = useMemo(() => {
    let result: undefined | { route: IStaticPageRoute; language: string };
    if (routesLoading) {
      return;
    }

    routes.forEach(route => {
      if (routeLang) {
        if (route.path.current === staticPath) {
          result = { route, language: routeLang };
        }
      } else {
        const staticLocalizedPageLanguage = Object.keys(PROD_SUPPORTED_LANGUAGES).find(lang => {
          const localePath = route?.localePath;
          return localePath && lang in localePath && localePath[lang]?.current === staticPath;
        });

        if (staticLocalizedPageLanguage) {
          result = { route, language: staticLocalizedPageLanguage };
        }

        if (route.path.current === staticPath) {
          result = { route, language: PROD_DEFAULT_LANGUAGE[region] };
        }
      }
    });

    return result;
  }, [routes, routesLoading, staticPath, region, routeLang]);

  useEffect(() => {
    if (!targetPage) {
      return;
    }
    if (targetPage.language !== currentLanguage) {
      setCurrentLocale(targetPage.language, region);
    }

    retrieveStaticPageById(
      targetPage.route._id,
      targetPage.route?.path?.current === appRoutes.nutritionExplorer.slice(1)
    );
  }, [targetPage, currentLanguage, retrieveStaticPageById, region, setCurrentLocale]);

  const is404 = useMemo(() => !routesLoading && !targetPage, [routesLoading, targetPage]);

  if (is404) {
    // If it is a SEO simplex URL navigate home.
    const fullPath = `/${staticPath}`;
    const wasRedirectedToSimplex = isRedirectedToSimplexUrl(fullPath);

    if (wasRedirectedToSimplex) {
      navigate(appRoutes.base);
      return (
        <BlankScreen
          context={{
            reason: 'Redirect to simple from static pages',
          }}
        />
      );
    }

    return <NotFound />;
  }

  const isTargetPageFetched = targetPage && targetPage?.route?._id === currentPage?._id;

  if (!currentPage) {
    return (
      <CardModalWithOptions>
        <LoyaltyLoader />
      </CardModalWithOptions>
    );
  }

  if (currentPage?.redirectUrl?.locale) {
    return <Redirect href={currentPage.redirectUrl.locale} />;
  }

  return (
    <CardModalWithOptions testID="static-page" helmetTitle={currentPage?.title}>
      {currentPage && isTargetPageFetched ? (
        <RenderCurrentStaticPage currentPage={currentPage} />
      ) : (
        <StaticPageSkeleton />
      )}
    </CardModalWithOptions>
  );
};

const CardModalWithOptions: CardModalProps = ({ children }, ...props) => {
  return (
    <CardModal
      alwaysShowHeader
      invertHeaderColor
      modalContainerStyle={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}
      noDesktopWebMaxWidth // static pages have headers built into HTML and this looks weird.
      isFullScreen
      {...props}
    >
      {children}
    </CardModal>
  );
};

/**
 * This function takes a pathname as param and render a Static Page component that can be embedded
 * inside a page / component so we're not limited to using routes for static content.
 */
export const StaticPageEmbedded = ({
  pathname,
  currentPageOverrideCss,
}: {
  pathname: string;
  currentPageOverrideCss?: string;
}) => {
  const { currentPage, getStaticPageByRouteId, is404 } = useGetStaticPageByRoute();

  useEffect(() => {
    getStaticPageByRouteId(pathname);
  }, [getStaticPageByRouteId, pathname]);

  if (is404) {
    return <NotFound />;
  }

  return (
    <Box testID="static-page-embeded" flex={1}>
      {currentPage ? (
        <RenderCurrentStaticPage
          currentPage={currentPage}
          currentPageOverrideCss={currentPageOverrideCss}
        />
      ) : (
        <LoadingContainer>
          <StaticPageSkeleton />
        </LoadingContainer>
      )}
    </Box>
  );
};

export default StaticPageContainer;
